import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col, message, Row, Space, Spin, Typography
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useMutation, useQuery, useQueryClient
} from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import ManagementInfo from 'common/components/ManagementInfo';
import ModalConfirm from 'common/components/ModalConfirm';
import SeoPlugin from 'common/components/SeoPlugin';
import SeoSection, { SeoSectionActionProps } from 'common/components/SeoSection';
import { SeoFormTypes } from 'common/components/SeoSection/types';
import StatusLabel, { StatusButtons } from 'common/components/StatusLabel';
import {
  createBlogCategoryByIdService,
  getBlogCategoryByIdService,
  updateBlogCategoryByIdService,
  updateBlogCategoryStatusService,
} from 'common/services/extends/blogs';
import { CreateUpdateBlogCategoryByIdParamsTypes } from 'common/services/extends/blogs/types';
import { ROUTE_PATHS, socialList } from 'common/utils/constant';
import {
  delay, generateSlug, returnDefaultOG, returnOgData
} from 'common/utils/functions';
import { updateNewsCategoriesByIdSchema } from 'common/utils/schemas';
import roles from 'configs/roles';

export type EditBlogFormTypes = {
  name: string;
  slug: string;
  description: string;
  displayOrder: number;
  status: number;
};

const EditBlogCategory: React.FC<ActiveRoles> = ({ roleCreate, roleUpdate, roleOther }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /* Selectors */
  const { languageOptions, defaultWebsiteLanguage } = useAppSelector((state) => state.system);

  /* States */
  const idParams = Number(searchParams.get('id'));
  const localeParams = searchParams.get('locale') || defaultWebsiteLanguage || '';
  const seoSectionRef = useRef<SeoSectionActionProps>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [confirm, setConfirm] = useState<string | undefined>(undefined);
  const [currentLang, setCurrentLang] = useState<string>(
    localeParams
  );
  const [status, setStatus] = useState<number>(1);

  /* Variables */
  const defaultValues = {
    name: '',
    slug: '',
    description: '',
    displayOrder: 1,
  };

  const method = useForm<EditBlogFormTypes>({
    resolver: yupResolver(updateNewsCategoriesByIdSchema),
    defaultValues
  });
  const { isDirty } = method.formState;

  /* Queries */
  const { data: categoryByIdData, isLoading } = useQuery(
    ['getBlogCategoryById', idParams, currentLang],
    () => {
      if (idParams) {
        return getBlogCategoryByIdService({
          id: idParams,
          locale: currentLang
        });
      }
      return undefined;
    },
    {
      enabled: !!(currentLang && idParams),
      refetchOnMount: 'always',
    }
  );

  const { mutate: updateBlogCateByIdMutate, isLoading: updateLoading } = useMutation(
    'updateBlogById',
    async (data: {
      id: number;
      params: CreateUpdateBlogCategoryByIdParamsTypes
    }) => updateBlogCategoryByIdService(data.id, data.params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(['getBlogCategoryById', idParams, currentLang]);
      },
      onError: () => {
        message.error(t('message.updateError'));
      }
    }
  );

  const { mutate: createBlogCateByIdMutate, isLoading: createLoading } = useMutation(
    'createBlog',
    createBlogCategoryByIdService,
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(`${ROUTE_PATHS.BLOG_CATEGORY_MANAGEMENT}`);
      },
      onError: () => {
        message.error(t('message.createError'));
      }
    }
  );

  const { mutate: updateStatusMutate, isLoading: updateStatusLoading } = useMutation(
    ['updateBlogStatus', idParams],
    async (data: {
      id: number, statusId: number
    }) => updateBlogCategoryStatusService(data.id, data.statusId),
    {
      onSuccess: (_, params) => {
        setStatus(params.statusId);
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(['getBlogCategoryById', idParams, currentLang]);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      }
    }
  );

  /* Functions */
  const defaultValueSEO: SeoFormTypes | undefined = useMemo(() => {
    if (!categoryByIdData) {
      return undefined;
    }
    const seo = categoryByIdData.seoData && categoryByIdData.seoData?.[`${currentLang}`];
    const og = categoryByIdData.ogData;
    return {
      seoTitle: seo?.title || '',
      seoIntro: seo?.description || '',
      seoKeyword: seo?.keywords || '',
      ogImage: seo?.image,
      metaRobot: seo?.metaRobot,
      metaViewPort: seo?.metaViewport,
      canonicalURL: seo?.canonicalUrl,
      structuredData: seo?.structureData,
      mediaSocial: returnDefaultOG(og, currentLang),
    } as SeoFormTypes;
  }, [currentLang, categoryByIdData]);

  const onSubmit = async (addingFn?: (id: number) => void) => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }
    const formData = method.getValues();
    const dataSeoForm = await seoSectionRef.current?.handleForm();

    const blogParams: CreateUpdateBlogCategoryByIdParamsTypes = {
      displayOrder: formData.displayOrder,
      status: formData.status,
      translations: {
        [currentLang]: {
          blogCategoryData: {
            name: formData.name,
            slug: formData.slug,
            description: formData.description
          }
        }
      },
      seoData: {
        [currentLang]: {
          title: dataSeoForm?.seoTitle,
          description: dataSeoForm?.seoIntro,
          keywords: dataSeoForm?.seoKeyword,
          metaRobot: dataSeoForm?.metaRobot,
          structureData: dataSeoForm?.structuredData,
          metaViewport: dataSeoForm?.metaViewPort,
          canonicalUrl: dataSeoForm?.canonicalURL,
          image: dataSeoForm?.ogImage
        }
      },
      ogData: returnOgData(currentLang, dataSeoForm?.mediaSocial)
    };

    if (idParams) {
      updateBlogCateByIdMutate({
        id: idParams,
        params: blogParams
      });
    } else {
      createBlogCateByIdMutate(blogParams, {
        onSuccess: (data) => addingFn && addingFn(data.id),
      });
    }
  };

  const changeLanguageAction = async (lang?: string) => {
    if (lang) {
      setConfirm(undefined);
      setLoadingTemplate(true);
      await delay(1000);
      setCurrentLang(lang as LanguageCodeTypes);
      setSearchParams({
        id: String(idParams),
        locale: lang,
      }, { replace: true });
      setLoadingTemplate(false);
    }
  };

  const handleChangeLang = (lang: string) => {
    if (isDirty) {
      setConfirm(lang);
    } else changeLanguageAction(lang);
  };

  const handleChangeStatusAndSave = async (stat: number) => {
    if (!idParams) {
      await onSubmit((id) => {
        updateStatusMutate({ id, statusId: stat });
      });
    } else {
      if (isDirty || seoSectionRef.current?.isFormDirty()) {
        await onSubmit();
      }
      updateStatusMutate({ id: idParams, statusId: stat });
    }
  };

  const submitForm = async () => {
    if (!idParams) {
      await onSubmit();
    } else {
      await onSubmit();
    }
    queryClient.invalidateQueries(['getBlogCategoryById', idParams, currentLang]);
  };

  /* Effects */
  useEffect(() => {
    if (categoryByIdData && categoryByIdData.translations?.[currentLang]) {
      setStatus(categoryByIdData.blogCategoryData.status);
      if (categoryByIdData.translations && categoryByIdData.translations[currentLang]) {
        const { name, slug, description } = categoryByIdData.translations[currentLang];
        const objDefault = {
          name,
          slug,
          description,
          displayOrder: categoryByIdData.blogCategoryData.displayOrder,
          status: categoryByIdData.blogCategoryData.status,
        };
        method.reset(objDefault);
      }
    } else method.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryByIdData, currentLang]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('blogDetail.editBlogCategory')}
        rightHeader={(
          <Space size={16}>
            {status && (
              <StatusButtons
                loading={updateStatusLoading}
                status={status}
                isApprove={roleOther.includes(roles.BLOG_CATE_APPROVED)}
                handleChangeStatus={(stat) => {
                  handleChangeStatusAndSave(stat);
                }}
              />
            )}
            {status !== 13 && (
              <Button
                type="primary"
                disabled={(idParams && !roleUpdate) || (!idParams && !roleCreate)}
                loading={updateLoading || createLoading}
                onClick={method.handleSubmit(submitForm)}
              >
                <SaveOutlined />
                {t('system.save')}
              </Button>
            )}
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <FormProvider {...method}>
          <Spin size="large" spinning={isLoading || updateLoading || createLoading || loadingTemplate}>
            <Row gutter={16}>
              <Col xxl={18} xl={16} lg={16}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Card type="inner">
                    {/* Tiêu đề  */}
                    <Row gutter={16}>
                      <Col span={12}>
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.title')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="name"
                            control={method.control}
                            defaultValue=""
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  if (!idParams
                                    || !categoryByIdData?.translations?.[currentLang]?.slug) {
                                    method.setValue('slug', generateSlug(e.currentTarget.value));
                                  }
                                }}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.slug')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="slug"
                            defaultValue=""
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                name="slug"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card>
                  <Card
                    type="inner"
                  >
                    <div className="site-card-border-less-wrapper">
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {/* Mô tả  */}
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.description')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="description"
                            control={method.control}
                            defaultValue=""
                            render={({ field, fieldState }) => (
                              <>
                                <TextArea
                                  {...field}
                                  className="u-mt-8"
                                  value={field.value}
                                  onChange={field.onChange}
                                  size="large"
                                  rows={6}
                                  style={{ minHeight: 50 }}
                                />
                                {fieldState.error && (
                                  <span
                                    className="a-input_errorMessage"
                                  >
                                    {fieldState.error.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </Space>
                    </div>
                  </Card>
                </Space>
                <SeoSection
                  canCreateComment={false}
                  defaultValues={defaultValueSEO}
                  ref={seoSectionRef}
                  socialList={socialList}
                />
              </Col>
              <Col xxl={6} xl={8} lg={8}>
                <div className="u-mb-16">
                  {status && <StatusLabel status={status} bigger />}
                </div>
                <div className="u-mb-16">
                  <Card>
                    <Typography.Text strong>
                      {t('blogDetail.displayOrder')}
                    </Typography.Text>
                    <Controller
                      name="displayOrder"
                      control={method.control}
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          className="u-mt-8"
                          type="number"
                          value={value}
                          onChange={onChange}
                          error={error?.message}
                          size="large"
                        />
                      )}
                    />
                  </Card>
                </div>
                <ManagementInfo
                  createdDate={categoryByIdData ? dayjs(categoryByIdData.blogCategoryData.createdAt).fromNow() : ''}
                  createdBy={categoryByIdData?.creator.name || ''}
                  lastUpdated={categoryByIdData ? dayjs(categoryByIdData.blogCategoryData.updatedAt).fromNow() : ''}
                  lastUpdatedBy={categoryByIdData?.updater.name || ''}
                  languageList={languageOptions}
                  currentLang={currentLang}
                  handleChangeLang={(
                    value
                  ) => value && handleChangeLang(value as LanguageCodeTypes)}
                />
                <SeoPlugin
                  commentList={[]}
                  handleOpenPreview={() => seoSectionRef.current?.handleOpenBrowserPreview()}
                  handleOpenSocialPreview={() => seoSectionRef.current?.handleOpenSocialPreview()}
                />
              </Col>
            </Row>
          </Spin>
        </FormProvider>
        <ModalConfirm
          isShow={!!confirm}
          handleCancel={() => setConfirm(undefined)}
          handleConfirm={() => changeLanguageAction(confirm)}
          handleClose={() => setConfirm(undefined)}
        >
          {t('message.confirmSkipInfo')}
        </ModalConfirm>
      </div>
    </>
  );
};

export default EditBlogCategory;
